import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { getLeadUid } from 'site-modules/shared/components/lead-form/utils/utils';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { getVehicleLabel } from 'site-modules/shared/components/lead-form/utils/get-vehicle-label';

export function DealerVinCheckAvailabilityCard({
  leadTarget,
  priceStrikethrough = false,
  isInStockOnline = false,
  specialIdentifier = '',
}) {
  const { dealer, inventory, leadAttributes } = leadTarget;

  const distance = get(leadAttributes, 'distance');
  const dealerInfo = get(inventory, 'dealerInfo', dealer);
  const { vin, type, vehicleInfo } = inventory;

  const salesOverallRating = get(dealerInfo, 'displayInfo.salesOverallRating', get(dealerInfo, 'salesRating', 0));
  const salesReviewCount = get(dealerInfo, 'displayInfo.salesReviewCount', get(dealerInfo, 'salesReviewCount', 0));
  const displayPrice = get(inventory, 'prices.displayPrice');

  const uid = getLeadUid(leadTarget);
  const isUsedVehicle = isUsed(type);
  const showPrice = !!displayPrice && (isUsedVehicle || priceStrikethrough);

  const vehicleMileage = get(vehicleInfo, 'mileage');

  return (
    <Fragment>
      <Row className="align-items-center">
        <Col xs={12}>
          <div className="mb-1_25 mb-md-1_75">
            <h3 className="fw-bold display-lg mb-0 text-primary-darker">
              {getVehicleLabel({ vehicleInfo, specialIdentifier })}
            </h3>
            {!!vin && <div className="medium text-gray-darker mt-0_5 mt-md-0_25">VIN: {vin}</div>}
            {isUsedVehicle && vehicleMileage && (
              <div className="medium text-gray-darker mt-0_25">Mileage: {numberWithCommas(vehicleMileage)}</div>
            )}
          </div>
        </Col>
        <Col xs={12}>
          <div className="d-flex justify-content-between align-items-end w-100">
            <div>
              <h4 className="medium text-gray-darker fw-bold mb-0_5" id={uid}>
                {dealerInfo.name}
              </h4>
              <div className="small">
                {!!salesReviewCount && (
                  <Fragment>
                    <RatingStars rating={salesOverallRating} className="text-primary-darker me-0_25" />
                    <span className="me-1 text-gray-dark">({salesReviewCount})</span>
                  </Fragment>
                )}
                {distance > 0 && (
                  <span className="text-gray-dark">
                    <span className="icon-location me-0_25 text-gray-darker" aria-hidden />
                    {isInStockOnline ? '(In-stock online)' : `(${numberWithCommas(distance)} mi)`}{' '}
                    <span className="visually-hidden">away</span>
                  </span>
                )}
              </div>
            </div>
            {showPrice && (
              <div className="text-end">
                <div className="small text-gray-dark">{isUsedVehicle ? 'List' : 'MSRP'}</div>
                {isUsedVehicle ? (
                  <span className="text-gray-darker fw-bold">{formatPriceString(displayPrice)}</span>
                ) : (
                  <Fragment>
                    <span className="visually-hidden">Price Reduced From</span>
                    <s className="text-gray-darker fw-bold">{formatPriceString(displayPrice)}</s>
                  </Fragment>
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Fragment>
  );
}

DealerVinCheckAvailabilityCard.propTypes = {
  leadTarget: PropTypes.shape({
    inventory: PropTypes.shape({}),
    dealer: PropTypes.shape({}),
    leadAttributes: PropTypes.shape({
      primary: PropTypes.bool.isRequired,
      selected: PropTypes.bool.isRequired,
      distance: PropTypes.number,
    }).isRequired,
  }).isRequired,
  priceStrikethrough: PropTypes.bool,
  isInStockOnline: PropTypes.bool,
  specialIdentifier: PropTypes.string,
};
